<template>
  <div>
    <!-- Selector de año -->
    <v-row class="mb-6 align-center">
      <v-col cols="auto">
        <h3 class="font-weight-bold" :style="{ color: color.primaryVariant }">
          Facturación por concepto — {{ selectedYear }}
        </h3>
      </v-col>
      <v-spacer />
      <v-col cols="6" sm="3" md="2">
        <v-select
          v-model="selectedYear"
          :items="yearOptions"
          label="Año"
          dense
          outlined
          hide-details
          @change="loadCharts"
        />
      </v-col>
    </v-row>

    <!-- Loader -->
    <div v-if="loading" class="d-flex justify-center py-12">
      <v-progress-circular indeterminate :color="color.primary" size="48" />
    </div>

    <!-- Gráfica -->
    <div v-else>
      <v-row>
        <!-- Gráfica combinada -->
        <v-col cols="12">
          <v-card flat outlined class="pa-4">
            <p class="caption text-uppercase font-weight-bold grey--text mb-4">
              Ingresos mensuales por concepto (MXN)
            </p>
            <BarChart
              chart-id="invoice-bar-chart"
              :chart-data="chartData"
              :chart-options="chartOptions"
              :styles="{ height: '380px' }"
              :height="380"
            />
          </v-card>
        </v-col>
      </v-row>

      <!-- Totales anuales por concepto -->
      <v-row class="mt-4">
        <v-col
          v-for="(card, i) in summaryCards"
          :key="i"
          cols="12"
          sm="4"
        >
          <v-card flat outlined class="pa-4 d-flex align-center">
            <v-avatar size="40" :color="card.color" class="mr-4">
              <v-icon dark small>{{ card.icon }}</v-icon>
            </v-avatar>
            <div>
              <p class="caption grey--text mb-0 text-uppercase font-weight-bold">
                {{ card.label }}
              </p>
              <p class="title font-weight-bold mb-0" :style="{ color: card.color }">
                ${{ card.total | currency }}
              </p>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { colors } from "@/constants/colors";
import BarChart from "@/components/dashboard/home/BarChart.vue";

const CONCEPT_META = {
  EVENTO: {
    label: "Eventos",
    color: "#24b2da",
    icon: "mdi-calendar",
    bgAlpha: "rgba(36, 178, 218, 0.7)",
  },
  MEMBRESIA: {
    label: "Membresías",
    color: "#7e57c2",
    icon: "mdi-card-account-details-outline",
    bgAlpha: "rgba(126, 87, 194, 0.7)",
  },
  PRODUCTO_SERVICIO: {
    label: "Productos / Servicios",
    color: "#26a69a",
    icon: "mdi-storefront-outline",
    bgAlpha: "rgba(38, 166, 154, 0.7)",
  },
};

export default {
  name: "InvoiceCharts",

  components: { BarChart },

  filters: {
    currency(val) {
      if (val == null) return "0.00";
      return Number(val)
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
  },

  data() {
    const currentYear = new Date().getFullYear();
    return {
      color: colors,
      loading: false,
      selectedYear: currentYear,
      yearOptions: Array.from({ length: 5 }, (_, i) => currentYear - i),
      chartsData: null,
    };
  },

  computed: {
    chartData() {
      if (!this.chartsData) {
        return { labels: [], datasets: [] };
      }
      const { months, series } = this.chartsData;
      return {
        labels: months,
        datasets: Object.entries(series).map(([concept, values]) => ({
          label: CONCEPT_META[concept]?.label || concept,
          backgroundColor: CONCEPT_META[concept]?.bgAlpha || "#999",
          borderColor: CONCEPT_META[concept]?.color || "#999",
          borderWidth: 1,
          data: values,
        })),
      };
    },

    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: "top" },
          tooltip: {
            callbacks: {
              label(ctx) {
                const val = ctx.parsed.y;
                return ` ${ctx.dataset.label}: $${Number(val)
                  .toFixed(2)
                  .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback(val) {
                return "$" + Number(val).toLocaleString("es-MX");
              },
            },
          },
        },
      };
    },

    summaryCards() {
      if (!this.chartsData) return [];
      return Object.entries(this.chartsData.series).map(([concept, values]) => ({
        label: CONCEPT_META[concept]?.label || concept,
        color: CONCEPT_META[concept]?.color || "#999",
        icon: CONCEPT_META[concept]?.icon || "mdi-currency-usd",
        total: values.reduce((sum, v) => sum + v, 0),
      }));
    },
  },

  async created() {
    await this.loadCharts();
  },

  methods: {
    ...mapActions("invoice", ["fetchOrganizationInvoiceCharts"]),

    async loadCharts() {
      this.loading = true;
      try {
        const res = await this.fetchOrganizationInvoiceCharts({
          year: this.selectedYear,
        });
        if (res.status) {
          this.chartsData = res.data;
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
